import { Radio as RadioSchema } from '@/schemas/sceneDataSchema';
import { UseFormReturn, Controller } from 'react-hook-form';
import {
  FormControl,
  FormHelperText,
  FormControlLabel,
  Radio,
  RadioGroup,
  styled,
  Box,
  Typography,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';
import { FormValues } from '@/constants/formTypes';
import useAnalytics, { eventName } from '@/hooks/use-analytics';

export const RadioButtonFormElement = ({
  form,
  component,
}: {
  form: UseFormReturn<FormValues>;
  component: RadioSchema;
}) => {
  const { trackClick } = useAnalytics();

  return (
    <Controller
      name={encodeFieldKey(component)}
      control={form.control}
      render={({ field: { onChange, value, ...field }, fieldState }) => (
        <FormControl
          sx={{ display: 'contents' }}
          error={!!fieldState.error}
        >
          <RadioGroup
            {...field}
            value={value ?? ''}
            onChange={(event, selected) => {
              trackClick(eventName(selected));
              onChange(event, selected);
            }}
            aria-labelledby={component.label}
          >
            {component.options.map((option) => (
              <StyledFormControlLabel
                labelPlacement='start'
                key={option.value.toString()}
                value={option.value}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {option.icon && <Icon>{option.icon}</Icon>}
                    <Typography variant='body1'>{option.label}</Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
          {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};

const StyledFormControlLabel = styled(FormControlLabel)`
  display: flex;
  justify-content: space-between;
  margin: 0;
  padding: 8px;
  padding-right: 4px;

  & + & {
    border-top: 1px solid var(--mui-palette-border-main);
  }
`;
